import {useEffect, useState} from 'react';
import {CreditRequestController} from '../../../api/credit/credit-request.controller';
import {CreditRequestClassificationEnum} from '../../../model/credit/credit-request-classification.enum';
import {CreditAccountSummaryModel} from '../../../model/credit/credit-account-summary.model';
import {CreditRequestModel} from '../../../model/credit/credit-request.model';
import {CreditRequestStatusEnum} from '../../../model/credit/credit-request-status.enum';
import {CreditSupportPageHooks} from './credit-support-page-hooks';
import {CreditRequestUtils} from '../utils/credit-request.utils';

type CreditRequestDrawerState = {
  request: CreditRequestModel
  account: CreditAccountSummaryModel | null

  estimateHours: string
  setEstimateHours: (value: string) => void
  deliveryDays: string
  setDeliveryDays: (value: string) => void
  classification: CreditRequestClassificationEnum | ''
  setClassification: (value: CreditRequestClassificationEnum | '') => void
  proposedSolution: string
  setProposedSolution: (value: string) => void
  estimateValid: boolean
  insufficientBalance: boolean
  balanceDeficit: number

  actualHours: string
  setActualHours: (value: string) => void
  actualHoursValid: boolean

  resolutionText: string
  setResolutionText: (value: string) => void

  rejectReason: string
  setRejectReason: (value: string) => void

  submitEstimate: () => Promise<void>
  acceptOnBehalf: () => Promise<void>
  rejectOnBehalf: () => Promise<void>
  saveActualHours: () => Promise<void>
  startWork: () => Promise<void>
  pauseRequest: () => Promise<void>
  resumeRequest: () => Promise<void>
  resolveRequest: () => Promise<void>
  cancelRequest: () => Promise<void>
}

export const useCreditRequestDrawerState = (
  initialRequest: CreditRequestModel,
  refresh: () => Promise<void>
): CreditRequestDrawerState => {
  const [request, setRequest] = useState<CreditRequestModel>(initialRequest)
  const [account] = CreditSupportPageHooks.useCreditAccount(initialRequest.organizationId)
  const [estimateHours, setEstimateHours] = useState<string>('')
  const [deliveryDays, setDeliveryDays] = useState<string>('')
  const [classification, setClassification] = useState<CreditRequestClassificationEnum | ''>('')
  const [proposedSolution, setProposedSolution] = useState<string>('')
  const [actualHours, setActualHours] = useState<string>(initialRequest.actualHoursSpent?.toString() ?? '')
  const [resolutionText, setResolutionText] = useState<string>('')
  const [rejectReason, setRejectReason] = useState<string>('')

  useEffect(() => {
    setRequest(initialRequest)
    setActualHours(initialRequest.actualHoursSpent?.toString() ?? '')
  }, [initialRequest])

  const estimateNum = parseFloat(estimateHours)
  const deliveryDaysNum = parseInt(deliveryDays, 10)
  const isBug = classification !== '' && classification.startsWith('BUG_')
  const estimateValid =
    !isNaN(estimateNum) && estimateNum > 0 && CreditRequestUtils.isHalfStep(estimateNum) &&
    !isNaN(deliveryDaysNum) && deliveryDaysNum > 0 &&
    classification !== ''
  const balance = account?.balanceHours ?? 0
  const insufficientBalance = estimateValid && !isBug && balance < estimateNum
  const balanceDeficit = insufficientBalance ? estimateNum - balance : 0

  const actualNum = parseFloat(actualHours)
  const actualHoursValid = !isNaN(actualNum) && actualNum >= 0 && CreditRequestUtils.isHalfStep(actualNum)

  const submitEstimate = async () => {
    await CreditRequestController.estimateCreditRequest(request.id, request.version, {
      hours: estimateNum,
      deliveryDays: deliveryDaysNum,
      classification: classification as CreditRequestClassificationEnum,
      proposedSolution: proposedSolution || null
    })
    await refresh()
    setEstimateHours('')
    setDeliveryDays('')
    setClassification('')
    setProposedSolution('')
  }

  const acceptOnBehalf = async () => {
    // Admin acts on behalf via internal endpoint? MVP: external requires customer auth.
    // For admin-on-behalf use estimate endpoint when require_estimate_acceptance=false flow,
    // here we rely on customer-side accept; placeholder noop refresh.
    await refresh()
  }

  const rejectOnBehalf = async () => {
    await refresh()
  }

  const saveActualHours = async () => {
    await CreditRequestController.updateCreditRequestActualHours(request.id, request.version, {actualHours: actualNum})
    await refresh()
  }

  const startWork = async () => {
    await CreditRequestController.startCreditRequest(request.id, request.version)
    await refresh()
  }

  const pauseRequest = async () => {
    await CreditRequestController.pauseCreditRequest(request.id, request.version)
    await refresh()
  }

  const resumeRequest = async () => {
    await CreditRequestController.resumeCreditRequest(request.id, request.version)
    await refresh()
  }

  const resolveRequest = async () => {
    await CreditRequestController.resolveCreditRequest(request.id, request.version, {
      actualHours: actualNum,
      resolution: resolutionText
    })
    await refresh()
    setResolutionText('')
  }

  const cancelRequest = async () => {
    await CreditRequestController.cancelCreditRequest(request.id, request.version)
    await refresh()
  }

  return {
    request,
    account,
    estimateHours, setEstimateHours,
    deliveryDays, setDeliveryDays,
    classification, setClassification,
    proposedSolution, setProposedSolution,
    estimateValid,
    insufficientBalance,
    balanceDeficit,
    actualHours, setActualHours,
    actualHoursValid,
    resolutionText, setResolutionText,
    rejectReason, setRejectReason,
    submitEstimate,
    acceptOnBehalf,
    rejectOnBehalf,
    saveActualHours,
    startWork,
    pauseRequest,
    resumeRequest,
    resolveRequest,
    cancelRequest,
  }
}

export const isPreDebitStatus = (status: CreditRequestStatusEnum): boolean =>
  status === CreditRequestStatusEnum.NEEDS_ESTIMATION
  || status === CreditRequestStatusEnum.WAITING_CUSTOMER_ACCEPTANCE
  || status === CreditRequestStatusEnum.ESTIMATE_REJECTED
